import {useLingui} from "@lingui/react";
import {Trans} from "@lingui/react/macro";
import copy from "copy-to-clipboard";
import {QRCodeSVG} from "qrcode.react";
import {useState, type ReactNode} from "react";
import {getAddDeviceLink, reauth} from "../../features/auth/client-auth";
import {addMessage} from "../../features/messenger/messenger";
import {useSetLang} from "../../i18n";
import type {GroupPreviewEntry, UserRepo} from "../../models/UserRepo";
import AppShell from "../../shared/ui/AppShell";
import Header from "../../shared/ui/Header";
import Spinner from "../../shared/ui/Spinner";
import {StorageKey, storageWrapper} from "../../storage";
import ThemePicker from "../../ThemePicker";
import {Col, css, Row, Text} from "../../ui/Box";
import Button from "../../ui/Button";
import {Input} from "../../ui/Input";

const Block = ({children, title}: {children: ReactNode; title: ReactNode}) => (
  <Col rounded="4" surface pa="16" sp="12">
    <Text as="h2" size="20">
      {title}
    </Text>
    {children}
  </Col>
);

const langs = [
  {key: "de", flag: "🇩🇪"},
  {key: "en", flag: "🇺🇸"},
] as const;

const LangPicker = () => {
  const [pending, setLang] = useSetLang();
  const {i18n} = useLingui();
  return (
    <Col sp="8">
      <Row sp="4">
        <Text as="h3" color="secondary" size="14_tight" weight="bold">
          <Trans>Language</Trans> {pending && <Spinner size="20" inline />}
        </Text>
      </Row>
      <Row sp="4">
        {langs.map(({key, flag}) => (
          <Button key={key} onClick={() => setLang(key)} active={i18n.locale === key}>
            {flag}
          </Button>
        ))}
      </Row>
    </Col>
  );
};

const PillCols = () => {
  const [value, setValue] = useState(storageWrapper.storageGet(StorageKey.PILL_SPACING) || "wide");
  const handleChange = (nextCols: "compact" | "wide") => {
    setValue(nextCols);
    storageWrapper.storageSet(StorageKey.PILL_SPACING, nextCols);
  };
  return (
    <Col sp="8">
      <Row sp="4">
        <Text as="h3" color="secondary" size="14_tight" weight="bold">
          <Trans>Item Spacing</Trans>
        </Text>
      </Row>
      <Row sp="24">
        <Row as="label" sp="4">
          <input
            type="radio"
            name="pillSpacing"
            value="compact"
            checked={value === "compact"}
            onChange={() => handleChange("compact")}
          />
          <Text as="h3" color="primary" size="14_tight" weight="bold">
            <Trans>Compact</Trans>
          </Text>
        </Row>
        <Row as="label" sp="4">
          <input
            type="radio"
            name="pillSpacing"
            value="wide"
            checked={value === "wide"}
            onChange={() => handleChange("wide")}
          />
          <Text as="h3" color="primary" size="14_tight">
            <Trans>Wide</Trans>
          </Text>
        </Row>
      </Row>
    </Col>
  );
};

const Settings = () => (
  <Block title={<Trans>Settings</Trans>}>
    <Col sp="8">
      <Text as="h3" color="secondary" size="14_tight" weight="bold">
        <Trans>Theme</Trans>
      </Text>
      <ThemePicker />
    </Col>
    <LangPicker />
    <PillCols />
  </Block>
);

const Share = ({userRepo}: {userRepo: UserRepo}) => {
  const [link, setLink] = useState<null | string>(null);
  const handleStart = async () => {
    setLink(await getAddDeviceLink({userRepo}));
  };

  const handleCopyClick = (url: string) => {
    copy(url);
    addMessage(<Trans>Url copied</Trans>);
  };

  return (
    <Block title={<Trans>Add device</Trans>}>
      {link ? (
        <Col sp="24">
          <Text>
            <Trans>Scan QR code or share link</Trans>
          </Text>
          <QRCodeSVG value={link} className={css({pa: "4"})} style={{backgroundColor: "#fff"}} />
          <Row sp="8">
            <Input value={link} readOnly />
            <Button onClick={() => handleCopyClick(link)}>
              <Trans>Copy</Trans>
            </Button>
          </Row>
          <Text>
            <Trans>Code is valid for 5 minutes.</Trans>
          </Text>
        </Col>
      ) : (
        <Col align="start">
          <Button onClick={handleStart}>
            <Trans>Start</Trans>
          </Button>
        </Col>
      )}
    </Block>
  );
};

const Reauth = () => {
  const deviceId = storageWrapper.storageGet(StorageKey.DEVICE_ID);
  const reauthSecret = storageWrapper.storageGet(StorageKey.REAUTH_SECRET);
  if (!deviceId || !reauthSecret) return null;

  return (
    <Block title={<Trans>Re-Auth</Trans>}>
      <Button onClick={reauth}>
        <Trans>Re-Auth</Trans>
      </Button>
    </Block>
  );
};

// Profile is opened from a group's menu tab, so back leads there
const useBackPath = (userRepo: UserRepo) => {
  const lastGroupId = storageWrapper.storageGet(StorageKey.LAST_GROUP_ID);
  const lastGroup = userRepo.Group.useGetAll()[lastGroupId!] as GroupPreviewEntry | undefined;
  return lastGroup?.shortId ? `/g/${lastGroup.shortId}/menu` : "/";
};

const Profile = ({userRepo}: {userRepo: UserRepo}) => {
  const backPath = useBackPath(userRepo);
  return (
    <AppShell
      header={
        <Header
          title={<Trans>Profile</Trans>}
          backButton={{to: backPath, label: <Trans id="nav.back">Back</Trans>}}
        />
      }
    >
      <Col px="12" sp="24" py="12">
        <Settings />
        <Share userRepo={userRepo} />
        <Reauth />
      </Col>
    </AppShell>
  );
};

export default Profile;
